import axios from 'axios';
import { v2Api, API_ROOT } from './v2Client';
import { ensureAccessToken } from './authTokens';
import { clubRequestHeaders } from '../currentClub';

const LOG = '[SquashAPI terrain]';


function normalizeTerrain(row) {
  if (!row) return null;
  const id = row.id ?? row.Id;
  if (id == null) return null;
  return {
    id: String(id),
    name: row.name ?? row.Name ?? '',
    number: row.number ?? row.Number ?? null,
    sport: row.sport ?? row.Sport ?? 'squash',
    sponsor: row.sponsor ?? row.Sponsor ?? '',
    sponsor_image_url: row.sponsor_image_url || null,
    // Chaîne YouTube du terrain (« Voir le live » de l'app) : gardée ici, sinon la fiche la relit vide et l'efface au
    // prochain enregistrement.
    youtube_channel_url: row.youtube_channel_url || '',
    is_active: row.is_active ?? row.isActive ?? row.IsActive ?? true,
    sort_order: Number(row.sort_order ?? row.sortOrder ?? row.SortOrder ?? 0) || 0,
    notes: row.notes ?? row.Notes ?? '',
    row_version: row.row_version ?? row.rowVersion ?? row.RowVersion ?? null,
    default_referee: normalizeReferee(row.default_referee),
    // Terrain d'une autre salle (tournoi importé, `includeExternal`) : « Les Marmots », « M1 » ; null = terrain du club.
    venue: row.venue || null,
    code: row.code || null,
  };
}

/** Arbitre par défaut d'un terrain (compte du téléphone du terrain) → option des sélecteurs d'arbitre. */
function normalizeReferee(r) {
  if (!r?.id) return null;
  return { id: String(r.id), first_name: r.first_name ?? '', last_name: r.last_name ?? '' };
}

/** GET /Terrain/list — `includeExternal` : aussi les terrains d'une autre salle d'un tournoi importé (M1…). */
export async function listTerrains({ includeExternal = false } = {}) {
  try {
    const { data } = await v2Api.get('/Terrain/list', includeExternal ? { params: { include_external: true } } : undefined);
    const rows = Array.isArray(data) ? data : [];
    return rows.map(normalizeTerrain).filter(Boolean);
  } catch (err) {
    console.error(LOG, 'list échoué', err);
    throw err;
  }
}

/** POST /Terrain/create */
export async function createTerrain(payload) {
  try {
    const { data } = await v2Api.post('/Terrain/create', payload);
    return normalizeTerrain(data) ?? data;
  } catch (err) {
    console.error(LOG, 'create échoué', { payload, err });
    throw err;
  }
}

/** POST /Terrain/update */
export async function updateTerrain(payload) {
  try {
    const { data } = await v2Api.post('/Terrain/update', payload);
    return normalizeTerrain(data) ?? data;
  } catch (err) {
    console.error(LOG, 'update échoué', { payload, err });
    throw err;
  }
}

/**
 * POST /Terrain/deactivate — refusé en 409 ({ error: 'terrain_has_future_reservations',
 * conflicts: [...] }) tant que des réservations futures existent sur ce terrain (voir plan §"Terrain
 * mis hors service"). L'appelant doit intercepter l'erreur pour afficher la liste des conflits.
 */
export async function deactivateTerrain(id) {
  try {
    const { data } = await v2Api.post('/Terrain/deactivate', { id: String(id) });
    return data;
  } catch (err) {
    console.error(LOG, 'deactivate échoué', { id, err });
    throw err;
  }
}

/** POST /Terrain/upload_sponsor_image (multipart) — logo du sponsor du terrain (JPEG/PNG/WebP, 4 Mo max) ; renvoie le terrain. */
export async function uploadTerrainSponsorImage(id, file) {
  const fd = new FormData();
  fd.append('id', String(id));
  fd.append('file', file);
  const access = await ensureAccessToken(API_ROOT);
  const headers = clubRequestHeaders();
  if (access) headers.Authorization = `Bearer ${access}`;
  try {
    const { data } = await axios.post(`${API_ROOT}/Terrain/upload_sponsor_image`, fd, {
      headers,
      maxContentLength: 6 * 1024 * 1024,
      maxBodyLength: 6 * 1024 * 1024,
    });
    return normalizeTerrain(data);
  } catch (err) {
    console.error(LOG, 'upload_sponsor_image échoué', { id, err });
    throw err;
  }
}

/** POST /Terrain/delete_sponsor_image — retire le logo du sponsor ; renvoie le terrain. */
export async function deleteTerrainSponsorImage(id) {
  try {
    const { data } = await v2Api.post('/Terrain/delete_sponsor_image', { id: String(id) });
    return normalizeTerrain(data);
  } catch (err) {
    console.error(LOG, 'delete_sponsor_image échoué', { id, err });
    throw err;
  }
}

/** GET /Terrain/default_referee/{n}?sport= — arbitre par défaut du terrain n° `number`, null si aucun. */
export async function getTerrainDefaultReferee(number, sport) {
  const { data } = await v2Api.get(`/Terrain/default_referee/${encodeURIComponent(String(number))}`, {
    params: sport ? { sport } : undefined,
  });
  return normalizeReferee(data?.referee);
}

export const terrainService = {
  defaultReferee: getTerrainDefaultReferee,
  list: listTerrains,
  create: createTerrain,
  update: updateTerrain,
  deactivate: deactivateTerrain,
  uploadSponsorImage: uploadTerrainSponsorImage,
  deleteSponsorImage: deleteTerrainSponsorImage,
};
